import type {PlaceDetails} from '@Components/google-map/google-map.types';
import {DEFAULT_LATITUDE, DEFAULT_LONGITUDE} from '@Components/google-map/google-map.types';
import {DEFAULT_LOCATION_ADDRESS} from '@Components/google-map/google-map.types';

export const getMapBoundsFromPosition = (position: google.maps.LatLngLiteral, boundRadiusKms = 1): google.maps.LatLngBoundsLiteral => {
  const lat = position.lat;
  const lng = position.lng;

  const earthRadiusKms = 6371;

  const latOffset = (boundRadiusKms / earthRadiusKms) * (180 / Math.PI);
  const lngOffset = (boundRadiusKms / (earthRadiusKms * Math.cos((Math.PI * lat) / 180))) * (180 / Math.PI);

  return {
    north: lat + latOffset,
    south: lat - latOffset,
    east: lng + lngOffset,
    west: lng - lngOffset,
  };
};

export async function getPlaceAddressByCords(location: google.maps.LatLng | google.maps.LatLngLiteral | google.maps.LatLngAltitudeLiteral): Promise<PlaceDetails | undefined> {
  await window.google.maps.importLibrary('geocoding');
  const geocoder = new window.google.maps.Geocoder();

  return geocoder.geocode({location}).then((response) => {
    if (response.results.length === 0) {
      return undefined;
    }
    return getPlaceById(response.results[0].place_id);
  });
}

export async function getPlaceById(id: string): Promise<PlaceDetails> {
  const placeLib = (await window.google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
  const place = new placeLib.Place({id});

  await place.fetchFields({fields: ['displayName', 'formattedAddress', 'location', 'addressComponents']});
  return extractPlaceDetailsFromPlace(place);
}

export const extractPlaceDetailsFromAutocompletePlaceResult = (
  autocompletePrediction: google.maps.places.AutocompletePrediction,
  placeResult: google.maps.places.PlaceResult
): PlaceDetails => {
  const addressComponents = placeResult.address_components ? getAddressComponents(placeResult.address_components) : undefined;

  return {
    name: autocompletePrediction.structured_formatting.main_text ?? undefined,
    placeID: autocompletePrediction.place_id,
    address: placeResult.formatted_address ?? undefined,
    longitude: placeResult.geometry?.location?.lng() ?? 0,
    latitude: placeResult.geometry?.location?.lat() ?? 0,
    ...addressComponents,
  };
};

export const extractPlaceDetailsFromPlace = (place: google.maps.places.Place): PlaceDetails => {
  const addressComponents = place.addressComponents ? getAddressComponents(place.addressComponents) : undefined;

  return {
    name: place.displayName ?? undefined,
    placeID: place.id,
    address: place.formattedAddress ?? undefined,
    longitude: place.location?.lng() ?? 0,
    latitude: place.location?.lat() ?? 0,
    ...addressComponents,
  };
};

export const getGoogleMapEmbedSrcLocation = (venue: PlaceDetails|undefined, apiKey: string): string => {
  const searchParam = getGoogleMapEmbedSearchParam(venue);
  const center = venue ? `${venue.latitude},${venue.longitude}` : `${DEFAULT_LATITUDE},${DEFAULT_LONGITUDE}`;

  return `https://maps.google.com/maps/embed/v1/place?key=${apiKey}&q=${searchParam}&center=${center}`;
}

export const getNewGoogleMapEmbedSrcLocation = (src: string | null, venue: PlaceDetails): string => {
  if (!src) {
    return '';
  }
  const urlObj = new URL(src);
  const params = new URLSearchParams(urlObj.search);
  params.set('q', getGoogleMapEmbedSearchParam(venue));
  urlObj.search = params.toString();
  return urlObj.toString();
};

const getGoogleMapEmbedSearchParam = (venue: PlaceDetails|undefined): string => {
  let searchParam = DEFAULT_LOCATION_ADDRESS;

  if (venue) {
    searchParam = venue.placeID ? `place_id:${venue.placeID}` : (venue.address ?? '');
  }
  return encodeURI(searchParam);
};

const isGeocoderAddressComponent = (component: google.maps.GeocoderAddressComponent | google.maps.places.AddressComponent): component is google.maps.GeocoderAddressComponent => {
  return (component as google.maps.GeocoderAddressComponent).long_name !== undefined;
};

const getAddressComponents = (
  addressComponents: (google.maps.GeocoderAddressComponent | google.maps.places.AddressComponent)[]
): {country: string; province: string; city: string; postalCode: string} => {
  const details = {
    country: '',
    province: '',
    city: '',
    postalCode: '',
  };

  for (const component of addressComponents) {
    const componentType = component.types[0];

    const componentName = isGeocoderAddressComponent(component) ? component.long_name : component.longText;

    switch (componentType) {
      case 'country':
        details.country = componentName ?? '';
        break;
      case 'administrative_area_level_1':
        details.province = componentName ?? '';
        break;
      case 'locality':
        details.city = componentName ?? '';
        break;
      case 'postal_code':
        details.postalCode = componentName ?? '';
        break;
      default:
        break;
    }
  }

  return details;
};
